import type {
  Tenant,
  TransactionOrigin,
  TransactionStatus,
} from '../../domain/entities/sales.entity';

/**
 * Filtros da tela de Vendas. Todos são aplicados **no servidor** — o
 * `/admin/sales/transactions` recebe cada um como query param, então filtrar
 * vale para a base inteira e o `total` da resposta já vem filtrado.
 *
 * O contato é só o `contactUserId`: quem traduz "nome ou e-mail" em usuário é
 * o `ContactSearchInput`, contra `GET /admin/identity/contacts`. O Sales filtra
 * por `Transaction.userId`, um campo que ele mesmo é dono — daí não haver
 * acoplamento com o Identity no back. O rótulo legível do contato **não** é
 * filtro: fica no `contact-label-cache`, fora da URL.
 */
export interface SalesTransactionFilters {
  contactUserId: string;
  externalId: string;
  businessUnit: Tenant | '';
  status: TransactionStatus | '';
  /** Id externo (gateway) da oferta — match exato, não substring. */
  offerExternalId: string;
  /** Id externo (gateway) do produto — match exato, não substring. */
  productExternalId: string;
  origin: TransactionOrigin | '';
  /** "Cobrada entre" — faixa de `occurredAt`, o instante no gateway. */
  occurredFrom: string;
  occurredTo: string;
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
}

export const EMPTY_SALES_FILTERS: SalesTransactionFilters = {
  contactUserId: '',
  externalId: '',
  businessUnit: '',
  status: '',
  offerExternalId: '',
  productExternalId: '',
  origin: '',
  occurredFrom: '',
  occurredTo: '',
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: '',
};

/** Quantos filtros o operador aplicou. */
export function countActiveSalesFilters(
  filters: SalesTransactionFilters,
): number {
  return Object.values(filters).filter(Boolean).length;
}

/** Os filtros como query params da listagem; vazio some. */
export function toSalesQueryFilters(filters: SalesTransactionFilters) {
  return {
    userId: filters.contactUserId || undefined,
    externalId: filters.externalId.trim() || undefined,
    status: filters.status || undefined,
    businessUnit: filters.businessUnit || undefined,
    offerExternalId: filters.offerExternalId.trim() || undefined,
    productExternalId: filters.productExternalId.trim() || undefined,
    origin: filters.origin || undefined,
    occurredFrom: filters.occurredFrom || undefined,
    occurredTo: filters.occurredTo || undefined,
    createdFrom: filters.createdFrom || undefined,
    createdTo: filters.createdTo || undefined,
    updatedFrom: filters.updatedFrom || undefined,
    updatedTo: filters.updatedTo || undefined,
  };
}

/**
 * A ordem da tela de Vendas. **Não é filtro**: fica fora de
 * `SalesTransactionFilters` para não contar no "N filtros aplicados" nem ser
 * apagada pelo "Limpar filtros". Vive na URL pelo mesmo motivo dos filtros —
 * sobreviver ao "Voltar" do detalhe.
 *
 * O default é o instante **no gateway**: ordenar pela escrita local punha um
 * ano de histórico reconstruído pelo backfill no topo, como se tivesse acabado
 * de acontecer (primum-core-root/docs/plans/in-review/ordenacao-da-listagem-de-vendas.md).
 */
export const SALES_SORT_OPTIONS = [
  { value: 'occurredAt', label: 'Data da cobrança' },
  { value: 'createdAt', label: 'Registrada em' },
  { value: 'updatedAt', label: 'Última atualização' },
] as const satisfies readonly { value: string; label: string }[];

export type SalesSortField = (typeof SALES_SORT_OPTIONS)[number]['value'];

export interface SalesSort {
  sortBy: SalesSortField;
  sortDirection: 'asc' | 'desc';
}

export const DEFAULT_SALES_SORT: SalesSort = {
  sortBy: 'occurredAt',
  sortDirection: 'desc',
};

const isSortField = (value: string): value is SalesSortField =>
  SALES_SORT_OPTIONS.some((option) => option.value === value);

/**
 * Lê a ordem da URL. Valor que o back não aceita (URL editada à mão, link
 * velho) cai no default em vez de virar 400 na listagem.
 */
export function readSalesSort(searchParams: URLSearchParams): SalesSort {
  const sortBy = searchParams.get('sortBy') ?? '';
  const sortDirection = searchParams.get('sortDirection');
  return {
    sortBy: isSortField(sortBy) ? sortBy : DEFAULT_SALES_SORT.sortBy,
    sortDirection:
      sortDirection === 'asc' || sortDirection === 'desc'
        ? sortDirection
        : DEFAULT_SALES_SORT.sortDirection,
  };
}
